import { Suspense, lazy } from 'react';
import { Box, Spinner } from '@cloudscape-design/components';
import { Navigate, createBrowserRouter } from 'react-router';
import App from './App';
import Launcher from './pages/home/Launcher';
import AboutSpine from './pages/about/AboutSpine';
import AboutStack from './pages/about/AboutStack';
import NotFound from './pages/NotFound';
import AiConfigAdmin from './pages/AiConfigAdmin';
import UsageAdmin from './pages/usage/UsageAdmin';
import DataAdmin from './pages/DataAdmin';
import EmailAdmin from './pages/EmailAdmin';
import AppRegistryAdmin from './pages/AppRegistryAdmin';
import RbacAdmin from './pages/RbacAdmin';
import AccessRequests from './pages/AccessRequests';
import OnboardApp from './pages/OnboardApp';
import OnboardSubmit from './pages/OnboardSubmit';
import OnboardMine from './pages/OnboardMine';
import OnboardingAdmin from './pages/OnboardingAdmin';
import RemoteRoute from './remotes/RemoteRoute';
import AuthCallback from './auth/AuthCallback';
import PostLogout from './auth/PostLogout';
import GraphCallback from './auth/GraphCallback';
import { RequirePermission } from './rbac/RequirePermission';
import { ASSISTANT_POPOUT_ROUTE_BASE } from './chat/popout';

/**
 * The assistant as a standalone window.
 *
 * LAZY, for exactly the reason App.tsx's drawer is lazy: `ChatPanel` reaches
 * `react-markdown` + `remark-gfm` through the SDK's `Markdown.tsx`. This router
 * module IS the entry chunk's route table, so a static import here would charge
 * the markdown stack to every page load — including every load that never opens
 * the assistant at all.
 */
const AssistantPopout = lazy(() => import('./chat/AssistantPopout'));

// Sentinel appId for a "not scoped to one app" permission check — matches
// the API's REGISTRY_LISTING_SENTINEL_APP_ID/RBAC_MANAGE_SENTINEL_APP_ID
// convention (registry.ts/rbac.ts): legitimate because both the `admin`
// role's `Registry:Manage`, `Rbac:Manage` and the platform `*:Manage` policies are org-wide
// (`application_id: []`), so the literal value doesn't matter.
const ORG_WIDE_PERMISSION_APP_ID = '*';

export const router = createBrowserRouter([
  { path: '/auth/callback', element: <AuthCallback /> },
  { path: '/auth/post-logout', element: <PostLogout /> },
  // Second, independent OAuth flow to Entra for delegated Graph people
  // search (ADR 0019) — a separate popup/iframe window, not the primary
  // login session, so it's its own top-level route outside the auth-gated
  // <App> tree (see AUTH_EXEMPT_PATHS in ./auth/postLoginRedirect).
  { path: '/auth/graph-callback', element: <GraphCallback /> },
  // The assistant popped out into its own window. Top-level, OUTSIDE the <App>
  // shell, because the whole point is no side navigation, no breadcrumbs and no
  // drawer — the window is the panel.
  //
  // Outside <App> is NOT outside auth: AuthProvider wraps RouterProvider in
  // main.tsx, so this route is gated exactly like every other page, and it is
  // deliberately absent from AUTH_EXEMPT_PATHS so an unauthenticated popout
  // runs the ordinary sign-in redirect and is returned here afterwards. No
  // token is ever passed in the URL (see ./chat/popout).
  //
  // It out-ranks the shell's `/:appId/*` remote route: React Router scores a
  // static segment above a dynamic one, so `/assistant/x` reaches this and an
  // app is unaffected at its own `/{appId}` path.
  {
    path: `${ASSISTANT_POPOUT_ROUTE_BASE}/:appId`,
    element: (
      <Suspense
        fallback={
          <Box padding="l" textAlign="center">
            <Spinner size="large" />
          </Box>
        }
      >
        <AssistantPopout />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <App />,
    children: [
      { index: true, element: <Launcher /> },
      // The AI Spine page (ADR 0028): every authenticated user, no permission
      // gate, fetches nothing. A static segment, so it out-ranks `/:appId/*`.
      { path: 'about', element: <AboutSpine /> },
      // Its tech stack detail page, linked from Under the hood. Same rules: every
      // authenticated user, no permission gate, fetches nothing.
      { path: 'about/stack', element: <AboutStack /> },
      // Every authenticated user — not gated by RequirePermission. Submitting a request needs no permission; what a
      // given user can approve is decided per-request by the API (ADR 0010).
      { path: 'access-requests', element: <AccessRequests /> },
      // Onboarding splash/intake/status — every authenticated user, no
      // permission needed (matches Access Requests above); the API decides
      // what a submitter vs. an admin may do per-route.
      { path: 'onboard', element: <OnboardApp /> },
      { path: 'onboard/submit', element: <OnboardSubmit /> },
      { path: 'onboard/mine', element: <OnboardMine /> },
      // Platform admin pages: gp-admin OR the in-app `admin` role's matching
      // permission (ADR 0041). The API independently enforces the same
      // requirePlatformPermission gate on every route these pages call.
      {
        path: 'admin/ai',
        element: (
          <RequirePermission permission="Ai:Manage" appId={ORG_WIDE_PERMISSION_APP_ID}>
            <AiConfigAdmin />
          </RequirePermission>
        ),
      },
      {
        path: 'admin/usage',
        element: (
          <RequirePermission permission="Usage:Manage" appId={ORG_WIDE_PERMISSION_APP_ID}>
            <UsageAdmin />
          </RequirePermission>
        ),
      },
      {
        path: 'admin/data',
        element: (
          <RequirePermission permission="Data:Manage" appId={ORG_WIDE_PERMISSION_APP_ID}>
            <DataAdmin />
          </RequirePermission>
        ),
      },
      // The three earlier pages are now tabs of the one Data page; keep their URLs working.
      { path: 'admin/data-sources', element: <Navigate to="/admin/data?tab=assistant" replace /> },
      { path: 'admin/data-catalog', element: <Navigate to="/admin/data?tab=systems" replace /> },
      { path: 'admin/data-access', element: <Navigate to="/admin/data?tab=apps" replace /> },
      {
        path: 'admin/email',
        element: (
          <RequirePermission permission="Email:Manage" appId={ORG_WIDE_PERMISSION_APP_ID}>
            <EmailAdmin />
          </RequirePermission>
        ),
      },
      // App registry: gp-admin OR the in-app `admin` role's Registry:Manage
      // permission (ADR 0010) — the API's PUT/enable/disable/delete routes
      // accept the same carve-out (requireAdminOrPermission, registry.ts).
      {
        path: 'admin/apps',
        element: (
          <RequirePermission permission="Registry:Manage" appId={ORG_WIDE_PERMISSION_APP_ID}>
            <AppRegistryAdmin />
          </RequirePermission>
        ),
      },
      // RBAC: gp-admin OR the in-app `admin` role's Rbac:Manage permission
      // (ADR 0012 decision 7a) — same carve-out as App Registry above, and
      // the same gate the API's role/alliance/audit-log routes already
      // accept (routes/rbac.ts). Previously gp-admin-only, which
      // contradicted Rbac:Manage's whole point
      // (an admin-role holder managing RBAC without needing gp-admin).
      {
        path: 'admin/rbac',
        element: (
          <RequirePermission permission="Rbac:Manage" appId={ORG_WIDE_PERMISSION_APP_ID}>
            <RbacAdmin />
          </RequirePermission>
        ),
      },
      // App onboarding review queue: same Registry:Manage carve-out as App
      // Registry above — approving/rejecting a submission and managing the
      // registry are the same authority.
      {
        path: 'admin/onboarding',
        element: (
          <RequirePermission permission="Registry:Manage" appId={ORG_WIDE_PERMISSION_APP_ID}>
            <OnboardingAdmin />
          </RequirePermission>
        ),
      },
      // The old App Inventory is now the App Registry table; keep its URL working.
      { path: 'admin/inventory', element: <Navigate to="/admin/apps" replace /> },
      // Registered remotes mount under /{appId}/* (resolved from the registry).
      { path: ':appId/*', element: <RemoteRoute /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
